import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { FilterCriteria } from '../../../../models/api/reports/filter-detail-response';
import { BaseComponent } from '../../../base/base.component';
import { RangeFieldComponent } from '../../../core/fields/range-field.component';
import { SelectFieldComponent } from '../../../core/fields/select-field.component';
import { FilterSection, ICriteria } from '../edit-filter.component';
import { ReportsService } from '../../../../services/reports/reports.service';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-examiner-criteria',
  imports: [CommonModule, RangeFieldComponent, SelectFieldComponent],
  template: `
    <div class="tw-pt-2">
      <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full">
        <app-select-field
          label="Examiner"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
          [control]="fg.controls.examiners"
          optionLabel="description"
          optionValue="value"
          [multiple]="true"
          [multipleFilter]="true"
          [chip]="true"
          [options]="examinerOptions"
          [showHelpText]="false"
          [loading]="loading"
        />

        <app-select-field
          label="Examiner office"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
          [control]="fg.controls.examinerOffices"
          optionLabel="description"
          optionValue="value"
          [multiple]="true"
          [multipleFilter]="true"
          [chip]="true"
          [options]="examinerOfficeOptions"
          [showHelpText]="false"
          [loading]="loading"
        />

        <app-select-field
          label="Processing office"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
          [control]="fg.controls.processingOffices"
          optionLabel="description"
          optionValue="value"
          [multiple]="true"
          [multipleFilter]="true"
          [chip]="true"
          [options]="processingOfficeOptions"
          [showHelpText]="false"
          [loading]="loading"
        />

        <app-select-field
          label="Examiner note type"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
          [control]="fg.controls.noteTypes"
          optionLabel="description"
          optionValue="value"
          [multiple]="true"
          [multipleFilter]="true"
          [chip]="true"
          [options]="noteTypeOptions"
          [showHelpText]="false"
          [loading]="loading"
        />

        <app-range-field
          label="Created date range"
          labelClasses="tw-basis-full md:tw-basis-[30%] tw-whitespace-nowrap"
          valueClasses="tw-basis-full md:tw-basis-[70%] tw-min-w-0 tw-overflow-hidden"
          [control]="fg.controls.dateFrom"
          [toControl]="fg.controls.dateTo"
          [compact]="true"
          [maxSpanDays]="null"
        />
      </div>
    </div>
  `,
  styles: `
    :host ::ng-deep app-select-field,
    :host ::ng-deep app-range-field {
      display: block;
      min-width: 0;
    }
    :host ::ng-deep .p-select-label > .option-label,
    :host ::ng-deep .p-multiselect-label .option-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `,
})
export class ExaminerCriteriaComponent
  extends BaseComponent
  implements OnInit, ICriteria
{
  @Input({ required: true })
  section: FilterSection;

  private reports = inject(ReportsService);

  examinerOptions: ListItem[] = [];
  examinerOfficeOptions: ListItem[] = [];
  processingOfficeOptions: ListItem[] = [];
  noteTypeOptions: ListItem[] = [];

  loading = true;

  fg = new FormGroup({
    examiners: new FormControl<string[]>([]),
    examinerOffices: new FormControl<string[]>([]),
    processingOffices: new FormControl<string[]>([]),
    noteTypes: new FormControl<string[]>([]),
    dateFrom: new FormControl<Date>(null),
    dateTo: new FormControl<Date>(null),
  });

  constructor() {
    super();
  }

  setExaminerFromCriteria(criteria: FilterCriteria[]) {
    const examiners = criteria
      .filter((c) => c.column === 'EXAMINER')
      .flatMap((c) => c.values);

    const offices = criteria
      .filter((c) => c.column === 'HOME_OFFICE')
      .flatMap((c) => c.values);

    const processingOffices = criteria
      .filter((c) => c.column === 'PROCESSING_OFFICE')
      .flatMap((c) => c.values);

    if (examiners.length) {
      this.fg.controls.examiners.setValue(examiners);
    }
    if (offices.length) {
      this.fg.controls.examinerOffices.setValue(offices);
    }
    if (processingOffices.length) {
      this.fg.controls.processingOffices.setValue(processingOffices);
    }
    const noteCriteria = criteria.find(
      (c) =>
        c.column === 'FILTER_NOTE_TYPE' ||
        c.column === 'FILTER_NOTE_DATE' ||
        c.column === 'FILTER_NOTE_TYPE_DATE',
    );

    if (noteCriteria) {
      const value = noteCriteria.values?.[0] || '';

      if (noteCriteria.column === 'FILTER_NOTE_TYPE') {
        const types = value.split(',').map((t) => t.replace(/'/g, ''));
        this.fg.controls.noteTypes.setValue(types);
      } else if (noteCriteria.column === 'FILTER_NOTE_DATE') {
        const [from, to] = value.split('|');
        this.fg.controls.dateFrom.setValue(this.parseDate(from));
        this.fg.controls.dateTo.setValue(this.parseDate(to));
      } else if (noteCriteria.column === 'FILTER_NOTE_TYPE_DATE') {
        const parts = value.split('|');
        const typesStr = parts[0];
        const from = parts[1];
        const to = parts[2];
        const types = typesStr.split(',').map((t) => t.replace(/'/g, ''));
        this.fg.controls.noteTypes.setValue(types);
        this.fg.controls.dateFrom.setValue(this.parseDate(from));
        this.fg.controls.dateTo.setValue(this.parseDate(to));
      }
    }
  }

  private parseDate(dateStr: string): Date | null {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    return new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
  }

  ngOnInit() {
    this.section.criteria = this;

    this.dispose.subs = this.fg.valueChanges.subscribe(() => this.emitCount());

    this.setupDateValidation(
      this.fg.controls.dateFrom,
      this.fg.controls.dateTo,
    );

    const client = this.reports.selectedClient();
    if (client) {
      this.loadExaminerData(Number(client));
    }
  }

  @HandleLoadingState()
  private async loadExaminerData(contractNumber: number) {
    const data =
      await this.api.filtersCriteria.getExaminerFilter(contractNumber);
    this.examinerOptions = data.examiners;
    this.examinerOfficeOptions = data.examinerOffices;
    this.processingOfficeOptions = data.processingOffices;
    this.noteTypeOptions = data.noteTypes;

    if (this.section.values?.length) {
      this.setExaminerFromCriteria(this.section.values);
    }
  }

  private setupDateValidation(
    from: FormControl<Date>,
    to: FormControl<Date>,
  ): void {
    this.dispose.subs = from.valueChanges.subscribe((value) => {
      if (value) {
        to.addValidators(Validators.required);
        to.markAsTouched();
      } else if (!to.value) {
        to.removeValidators(Validators.required);
      }
      to.updateValueAndValidity({ emitEvent: false });
    });

    this.dispose.subs = to.valueChanges.subscribe((value) => {
      if (value) {
        from.addValidators(Validators.required);
        from.markAsTouched();
      } else if (!from.value) {
        from.removeValidators(Validators.required);
      }
      from.updateValueAndValidity({ emitEvent: false });
    });
  }

  private emitCount() {
    let count =
      (this.fg.controls.examiners.value?.length || 0) +
      (this.fg.controls.examinerOffices.value?.length || 0) +
      (this.fg.controls.processingOffices.value?.length || 0) +
      (this.fg.controls.noteTypes.value?.length || 0);

    if (this.fg.controls.dateFrom.value && this.fg.controls.dateTo.value) {
      count++;
    }

    this.section.count.set(count);
  }

  valid(): boolean {
    return this.fg.valid;
  }

  getCriteria(): FilterCriteria[] {
    const examinerCriteria = (this.fg.controls.examiners.value || []).map(
      (value) =>
        ({
          tableAlias: 'claim_examiner_as_of',
          column: 'EXAMINER',
          displayName: '',
          operator: 'EQUALS',
          values: [value],
          lineOfBusiness: '',
          dataType: 'VARCHAR2',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Examiner',
          filterSubsection: '',
        }) as FilterCriteria,
    );

    const examinerOfficeCriteria = (
      this.fg.controls.examinerOffices.value || []
    ).map(
      (value) =>
        ({
          tableAlias: 'claim_examiner_as_of',
          column: 'HOME_OFFICE',
          displayName: '',
          operator: 'EQUALS',
          values: [value],
          lineOfBusiness: '',
          dataType: 'VARCHAR2',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Examiner',
          filterSubsection: '',
        }) as FilterCriteria,
    );

    const processingOfficeCriteria = (
      this.fg.controls.processingOffices.value || []
    ).map(
      (value) =>
        ({
          tableAlias: 'claim',
          column: 'PROCESSING_OFFICE',
          displayName: '',
          operator: 'EQUALS',
          values: [value],
          lineOfBusiness: '',
          dataType: 'NUMBER',
          isClaimLab: false,
          formattedOperator: '',
          formattedValue: '',
          filterSection: 'Examiner',
          filterSubsection: '',
        }) as FilterCriteria,
    );

    const noteCriteria = this.getNoteCriteria();

    return [
      ...examinerCriteria,
      ...examinerOfficeCriteria,
      ...processingOfficeCriteria,
      ...(noteCriteria ? [noteCriteria] : []),
    ];
  }

  private getNoteCriteria(): FilterCriteria | null {
    const noteTypes = this.fg.controls.noteTypes.value || [];
    const dateFrom = this.fg.controls.dateFrom.value;
    const dateTo = this.fg.controls.dateTo.value;

    const hasNoteTypes = noteTypes.length > 0;
    const hasDates = dateFrom && dateTo;

    if (!hasNoteTypes && !hasDates) {
      return null;
    }

    const formatDate = (date: Date): string => {
      return `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`;
    };

    let column: string;
    let values: string[];

    if (hasNoteTypes && hasDates) {
      column = 'FILTER_NOTE_TYPE_DATE';
      const typesStr = noteTypes.map((t) => `'${t}'`).join(',');
      const datesStr = `${formatDate(dateFrom)}|${formatDate(dateTo)}`;
      values = [`${typesStr}|${datesStr}`];
    } else if (hasDates && !hasNoteTypes) {
      column = 'FILTER_NOTE_DATE';
      values = [`${formatDate(dateFrom)}|${formatDate(dateTo)}`];
    } else {
      column = 'FILTER_NOTE_TYPE';
      values = [noteTypes.map((t) => `'${t}'`).join(',')];
    }

    return {
      tableAlias: 'claim',
      column: column,
      displayName: '',
      operator: 'EXISTS',
      values: values,
      lineOfBusiness: '',
      dataType: 'VARCHAR2',
      isClaimLab: false,
      formattedOperator: '',
      formattedValue: '',
      filterSection: 'Examiner',
      filterSubsection: '',
    };
  }

  clearCriteria(): void {
    this.fg.controls.examiners.setValue([], { emitEvent: false });
    this.fg.controls.examinerOffices.setValue([], { emitEvent: false });
    this.fg.controls.processingOffices.setValue([], { emitEvent: false });
    this.fg.controls.noteTypes.setValue([], { emitEvent: false });
    this.fg.controls.dateFrom.setValue(null, { emitEvent: false });
    this.fg.controls.dateTo.setValue(null, { emitEvent: false });
    this.fg.controls.dateFrom.removeValidators(Validators.required);
    this.fg.controls.dateTo.removeValidators(Validators.required);
    this.fg.controls.dateFrom.markAsUntouched();
    this.fg.controls.dateFrom.markAsPristine();
    this.fg.controls.dateTo.markAsUntouched();
    this.fg.controls.dateTo.markAsPristine();
    this.section.count.set(0);
  }
}
